import { Fund } from 'src/components/Exchange/types';
import {
  AGREEMENT_TYPES,
  BUY_TRANSACTION,
  EXCHANGE_TRANSACTION,
  FundNoteAutoExchange,
  MONEY_MARKET_EVENT_FEE,
  MONEY_MARKET_EVENT_GATE,
  MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
  SELL_TRANSACTION,
  SystemCode,
} from 'src/settings/settings';
import { UserFund } from 'src/types';
import { updateAutoRMDFundNotes } from '../autoSell/auto-sell-fund-notes-utility';
import {
  isIRA,
  isPrimaryParty,
  isReccuringExchangeIRAMatching,
  isRetirementFund,
  isUserCountryRestricted,
} from '../funds-utility';
import {
  checkFoundFeaturesByCode,
  DISABLE_ON_MATCH,
  hasRequiredServiceFeatures,
  hasZeroBalance,
  isClosedFund,
  isRolloverOrTraditionalIRA,
  updateFundNote,
} from '../shared/shared-fund-notes-utility';

/**
 * Creates function for looking up exchange specific server codes
 */
function checkFoundFeaturesByCodeForExchange(
  fund: Fund | UserFund,
  serviceCode: number
): boolean {
  return checkFoundFeaturesByCode(fund, serviceCode, true);
}

/**
 * Adds fund notes for recurring exchange from funds.
 *
 * @param {AutoExchangeFund} fund
 * @returns {AutoExchangeFund}
 */
export function updateAutoExchangeFromFundNotes(
  fund: Fund | UserFund,
  personalData,
  rmds
) {
  updateAutoRMDFundNotes(fund, rmds);

  // Zero balance
  updateFundNote(
    fund,
    fund.balanceAsOf === 0, // balance includes uncollected shares
    FundNoteAutoExchange.FROM_ZERO,
    DISABLE_ON_MATCH
  );

  // Restricted-requires permanent US Address
  updateFundNote(
    fund,
    isUserCountryRestricted(personalData),
    FundNoteAutoExchange.COUNTRY_RESTRICTED,
    DISABLE_ON_MATCH
  );

  // ESA Plan
  updateFundNote(
    fund,
    fund.agreementType === 'EDUCATION_SAVINGS_ACCOUNT',
    FundNoteAutoExchange.ESA_PLAN,
    DISABLE_ON_MATCH
  );

  // Transaction Pending and not disabled
  updateFundNote(
    fund,
    fund.hasPendingTransaction && fund.redeemableFundDollars > 0,
    FundNoteAutoExchange.PENDING
  );

  // Transaction Pending
  updateFundNote(
    fund,
    fund.hasPendingTransaction && fund.redeemableFundDollars === 0,
    FundNoteAutoExchange.PENDING_TRANSACTION,
    DISABLE_ON_MATCH
  );

  // MMR - Redemption Block
  updateFundNote(
    fund,
    !!(
      fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod === MONEY_MARKET_EVENT_PERIOD_IN_EVENT
    ),
    FundNoteAutoExchange.MONEY_MARKET_EVENT_GATE_ACTIVE,
    DISABLE_ON_MATCH
  );

  // MMR - Liquidity Fee
  updateFundNote(
    fund,
    !!(
      fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod === MONEY_MARKET_EVENT_PERIOD_IN_EVENT
    ),
    FundNoteAutoExchange.MONEY_MARKET_EVENT_FEE_ACTIVE
  );

  // MMR - Redemption Block Removed
  updateFundNote(
    fund,
    !!(
      fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod !== MONEY_MARKET_EVENT_PERIOD_IN_EVENT
    ),
    FundNoteAutoExchange.MONEY_MARKET_EVENT_GATE_REMOVED
  );

  // MMR - Liquidity Fee Removed
  updateFundNote(
    fund,
    !!(
      fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod !== MONEY_MARKET_EVENT_PERIOD_IN_EVENT
    ),
    FundNoteAutoExchange.MONEY_MARKET_EVENT_FEE_REMOVED
  );

  // Stop Transfer
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.STOP_TRANSFER_ID),
    FundNoteAutoExchange.STOP_TRANSFER,
    DISABLE_ON_MATCH
  );

  // Unavailble for online redemption
  updateFundNote(
    fund,
    false, // All are available for online redemption
    FundNoteAutoExchange.ONLINE_PURCHASE_RESTRICTED,
    DISABLE_ON_MATCH
  );

  // Missing required service features
  updateFundNote(
    fund,
    !hasRequiredServiceFeatures(fund),
    FundNoteAutoExchange.UNAVAILABLE,
    DISABLE_ON_MATCH
  );

  // Return Mail - verify address
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.RETURN_MAIL_VERIFY_ADDRESS
    ),
    FundNoteAutoExchange.RETURN_MAIL_VERIFY_ADDRESS,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop All
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_ALL
    ),
    FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_ALL,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Exchange
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_EXCHANGE
    ),
    FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_EXCHANGE,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Balance
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_BALANCE
    ),
    FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_BALANCE,
    DISABLE_ON_MATCH
  );

  // Autmoated Systems Stop Check
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_CHECK
    ),
    FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_CHECK,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Purchase
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_PURCHASE
    ) || fund?.openToSubsequentInvestments === false,
    FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_PURCHASE,
    DISABLE_ON_MATCH
  );

  // Penalty Withholding
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(fund, SystemCode.PENALTY_WITHOLDING),
    FundNoteAutoExchange.PENALTY_WITHHOLDING,
    DISABLE_ON_MATCH
  );
  // Recurring Buy
  updateFundNote(
    fund,
    fund.recurringTransactionTypes.includes(BUY_TRANSACTION),
    FundNoteAutoExchange.AUTO_BUY
  );

  // Recurring Exchange
  updateFundNote(
    fund,
    fund.recurringTransactionTypes.includes(EXCHANGE_TRANSACTION) &&
      fund.agreementType !== 'COLLEGE_SAVINGS',
    FundNoteAutoExchange.AUTO_EXCHANGE
  );

  // Recurring Sell
  updateFundNote(
    fund,
    fund.recurringTransactionTypes.includes(SELL_TRANSACTION) &&
      fund.agreementType !== 'COLLEGE_SAVINGS',
    FundNoteAutoExchange.AUTO_SELL
  );

  // IRA Automax
  updateFundNote(fund, fund.hasIRAAutoMax, FundNoteAutoExchange.IRA_AUTOMAX);

  // Inherited fund
  updateFundNote(
    fund,
    fund.agreementType === AGREEMENT_TYPES.TRADITIONAL_IRA_DECEASED_BENE ||
      fund.agreementType === AGREEMENT_TYPES.ROTH_IRA_DECEASED_BENE,
    FundNoteAutoExchange.INHERITED_FUND,
    DISABLE_ON_MATCH
  );

  // Closed Fund
  updateFundNote(
    fund,
    hasZeroBalance(fund),
    FundNoteAutoExchange.CLOSED_FUND,
    DISABLE_ON_MATCH
  );
}

/**
 * Adds fund notes for recurring exchange to funds.
 *
 * @param {UserFund} fund
 */
export function updateAutoExchangeToFundNotes(
  fund: UserFund,
  exchangeFromFirstFund: UserFund
) {
  const shouldShowIRANote = isIRA(exchangeFromFirstFund)
    ? exchangeFromFirstFund.registrationGroup !== fund.registrationGroup &&
      !(isReccuringExchangeIRAMatching(fund) && isPrimaryParty(fund))
    : false;

  const shouldShowNonIRANote = !isIRA(exchangeFromFirstFund)
    ? exchangeFromFirstFund.registrationGroup !== fund.registrationGroup
    : false;

  // const shouldDisableIfPartOfAutoExchange =

  updateFundNote(
    fund,
    shouldShowNonIRANote,
    FundNoteAutoExchange.NON_IRA_MATCHING,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    shouldShowIRANote,
    FundNoteAutoExchange.IRA_MATCHING,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    !isRetirementFund(exchangeFromFirstFund) && isRetirementFund(fund),
    FundNoteAutoExchange.NONRETIREMENT_TO_RETIREMENT,
    DISABLE_ON_MATCH
  );

  // Closed Fund, not open to subsequent investment
  // Do not display fund note, or disable fund if it is closed allows subsequent investments
  updateFundNote(
    fund,
    isClosedFund(fund) && hasZeroBalance(fund),
    FundNoteAutoExchange.CLOSED_NEW,
    DISABLE_ON_MATCH
  );

  // Closed Fund
  updateFundNote(
    fund,
    hasZeroBalance(fund),
    FundNoteAutoExchange.CLOSED_FUND,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop All
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_ALL
    ),
    FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_ALL,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Balance
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_BALANCE
    ),
    FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_BALANCE,
    DISABLE_ON_MATCH
  );

  // Autmoated Systems Stop Check
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_CHECK
    ),
    FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_CHECK,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Purchase
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_PURCHASE
    ) || fund?.openToSubsequentInvestments === false,
    FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_PURCHASE,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Exchange
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_EXCHANGE
    ),
    FundNoteAutoExchange.AUTOMATED_SYSTEMS_STOP_EXCHANGE,
    DISABLE_ON_MATCH
  );

  // Return Mail - Verify Address
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.RETURN_MAIL_VERIFY_ADDRESS
    ),
    FundNoteAutoExchange.RETURN_MAIL_VERIFY_ADDRESS,
    DISABLE_ON_MATCH
  );

  // Stop Purchase
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(fund, SystemCode.STOP_PURCHASE),
    FundNoteAutoExchange.STOP_PURCHASE,
    DISABLE_ON_MATCH
  );

  // Purchase Block
  updateFundNote(
    fund,
    fund.purchaseBlocked,
    FundNoteAutoExchange.PURCHASED_BLOCKED,
    DISABLE_ON_MATCH
  );

  // Recurring Buy
  updateFundNote(
    fund,
    fund.recurringTransactionTypes.includes(BUY_TRANSACTION),
    FundNoteAutoExchange.AUTO_BUY
  );

  // Recurring Exchange
  /* This is disabled because we have created a new auto exchange condition that is being checked
     in the autoExchangeTransaction.selectors.js file
  updateFundNote(
    fund,
    fund.recurringTransactionTypes.includes(EXCHANGE_TRANSACTION) &&
      fund.agreementType !== 'COLLEGE_SAVINGS',
    FundNoteAutoExchange.AUTO_EXCHANGE
  );
  */

  // Recurring Sell
  updateFundNote(
    fund,
    fund.recurringTransactionTypes.includes(SELL_TRANSACTION) &&
      fund.agreementType !== 'COLLEGE_SAVINGS',
    FundNoteAutoExchange.AUTO_SELL
  );

  // IRA Automax
  updateFundNote(fund, fund.hasIRAAutoMax, FundNoteAutoExchange.IRA_AUTOMAX);

  // Unavailble for online redemption
  updateFundNote(
    fund,
    false, // All are available for online redemption
    FundNoteAutoExchange.ONLINE_PURCHASE_RESTRICTED,
    DISABLE_ON_MATCH
  );

  // Stop Purchase
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(fund, SystemCode.STOP_PURCHASE),
    FundNoteAutoExchange.STOP_PURCHASE,
    DISABLE_ON_MATCH
  );

  // Fiduciary, Non-Fiduciary
  // Can't exchange across fid and non-fid accounts
  updateFundNote(
    fund,
    false,
    FundNoteAutoExchange.FID_NON_FID,
    DISABLE_ON_MATCH
  );

  // Cannot exchange from taxable account into IRA
  updateFundNote(
    fund,
    false,
    FundNoteAutoExchange.TAXABLE_TO_IRA,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    !fund.newFund &&
      !fund.canExchange &&
      !(
        exchangeFromFirstFund &&
        isRolloverOrTraditionalIRA(exchangeFromFirstFund) &&
        fund.agreementType === 'ROTH_IRA'
      ),
    FundNoteAutoExchange.NOT_ELIGIBLE,
    DISABLE_ON_MATCH
  );
}
